import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import QRCode from 'qrcode'
import type { CampaignMember, Invite } from '@shared/types'
import { ApiError, api } from '@/lib/api'
import { useAsync } from '@/lib/useAsync'
import { useAuth } from '@/lib/auth'
import { PartyCard } from '@/components/PartyCard'
import { StoryTab } from '@/components/campaign/StoryTab'
import { NpcTab } from '@/components/campaign/NpcTab'
import { FundsTab } from '@/components/campaign/FundsTab'
import { InitiativeTab } from '@/components/campaign/InitiativeTab'
import { LootTab } from '@/components/campaign/LootTab'
import { AssistantTab } from '@/components/campaign/AssistantTab'
import { DmDashboard } from '@/components/dm/DmDashboard'
import { RollFeed } from '@/components/dice/RollFeed'
import { DiceRoller } from '@/components/dice/DiceRoller'
import { useRollFeed } from '@/lib/useRollFeed'
import { cn } from '@/lib/cn'
import {
  Badge, Button, EmptyState, ErrorNote, Field, Modal, PageLoader, Panel, SectionHeading, Select,
} from '@/components/ui'

const ROLE_TONE = { dm: 'gold', player: 'arcane', guest: 'neutral' } as const

export default function CampaignDetail() {
  const { campaignId = '' } = useParams()
  const { user } = useAuth()
  const navigate = useNavigate()
  const { data, error, loading, reload } = useAsync(() => api.getCampaign(campaignId), [campaignId])
  const { data: inviteData, loading: invitesLoading, reload: reloadInvites } = useAsync(() => api.listInvites(campaignId), [campaignId])
  const [actionError, setActionError] = useState<string | null>(null)
  const [creatingQuickJoin, setCreatingQuickJoin] = useState(false)
  const quickJoinRequestedRef = useRef(false)
  // The open tab lives in the URL so it survives a reload and can be linked to.
  const [params, setParams] = useSearchParams()
  // Owned here so the floating roller can push straight into the feed.
  const feed = useRollFeed(campaignId)

  const { campaign, myRole, members, characters } = data ?? {
    campaign: null,
    myRole: null,
    members: [],
    characters: [],
  }
  const isDm = myRole === 'dm'
  // A player can have more than one character — a backup, a second PC. Taking
  // only the first silently pointed this button at whichever row came back first.
  const myCharacters = characters.filter((c) => c.userId === user?.id)
  const activeInvites = inviteData?.invites.filter((invite) => !invite.revokedAt) ?? []
  const quickJoinInvite = activeInvites[0]

  useEffect(() => {
    if (!data || !isDm || invitesLoading || creatingQuickJoin || quickJoinInvite || quickJoinRequestedRef.current) return

    quickJoinRequestedRef.current = true
    void createQuickJoinInvite()
  }, [data, isDm, invitesLoading, creatingQuickJoin, quickJoinInvite])

  async function createQuickJoinInvite() {
    setCreatingQuickJoin(true)
    try {
      await api.createInvite(campaignId, { role: 'player', maxUses: null, expiresInDays: 14 })
      await reloadInvites()
    } catch (err) {
      quickJoinRequestedRef.current = false
      setActionError(err instanceof ApiError ? err.message : 'Could not create a join code')
    } finally {
      setCreatingQuickJoin(false)
    }
  }

  // A DM's default view is the screen they run a session from; players land on
  // the party roster, which is what they came for.
  const defaultTab = data?.myRole === 'dm' ? 'run' : 'party'
  const requested = params.get('tab') ?? defaultTab
  // DM-only tabs must not leave a player staring at a blank page if they follow
  // a shared link or edit the query string.
  const dmOnlyTabs = ['run', 'assistant']
  const tab = dmOnlyTabs.includes(requested) && data?.myRole !== 'dm' ? defaultTab : requested
  const setTab = (next: string) =>
    setParams(next === defaultTab ? {} : { tab: next }, { replace: true })

  if (loading) return <PageLoader />
  if (error || !data || !campaign) return <ErrorNote>{error ?? 'Campaign not found'}</ErrorNote>

  async function guard(action: () => Promise<unknown>) {
    setActionError(null)
    try {
      await action()
      reload()
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'That did not work')
    }
  }

  return (
    <div className="space-y-8">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2.5">
            <Link to="/" className="text-sm text-ink-500 transition-colors hover:text-ink-300">← Campaigns</Link>
            <Badge tone={ROLE_TONE[myRole]}>{myRole}</Badge>
          </div>
          <h1 className="mt-3 text-display">{campaign.name}</h1>
          {campaign.blurb && (
            <p className="mt-3 max-w-2xl text-[15px] leading-relaxed text-ink-400">{campaign.blurb}</p>
          )}
        </div>

        {myRole !== 'guest' && (
          myCharacters.length === 0 ? (
            <Button variant="primary" onClick={() => navigate(`/campaigns/${campaignId}/characters/new`)}>
              Create a character
            </Button>
          ) : (
            <div className="flex flex-wrap justify-end gap-2">
              {/* With one character it is the obvious action; with several they
                  are peers, so none of them gets the emphasis. */}
              {myCharacters.slice(0, 3).map((character) => (
                <Button
                  key={character.id}
                  variant={myCharacters.length === 1 ? 'primary' : 'secondary'}
                  onClick={() => navigate(`/characters/${character.id}`)}
                >
                  {myCharacters.length === 1 ? `Open ${character.name}` : character.name}
                </Button>
              ))}
              {myCharacters.length > 3 && (
                <Button onClick={() => setTab('party')}>
                  +{myCharacters.length - 3} more
                </Button>
              )}
            </div>
          )
        )}
      </header>

      <nav aria-label="Campaign sections" className="-mx-4 overflow-x-auto px-4">
        <div className="flex w-max gap-1 rounded-2xl bg-ink-900/60 p-1">
          {[
            ...(isDm ? [{ id: 'run', label: 'Run the session' }] : []),
            { id: 'party', label: 'Party', count: characters.length },
            { id: 'story', label: 'Story log' },
            { id: 'npcs', label: 'NPCs' },
            { id: 'loot', label: 'Loot' },
            { id: 'funds', label: 'Funds' },
            { id: 'initiative', label: 'Initiative' },
            { id: 'dice', label: 'Dice', count: feed.rolls.length || undefined },
            ...(isDm ? [{ id: 'assistant', label: 'Assistant' }] : []),
            { id: 'table', label: 'At the table', count: members.length },
          ].map((entry) => (
            <button
              key={entry.id}
              type="button"
              onClick={() => setTab(entry.id)}
              aria-current={tab === entry.id ? 'page' : undefined}
              className={cn(
                'pressable whitespace-nowrap rounded-xl px-3.5 py-2 text-sm font-medium',
                tab === entry.id
                  ? 'bg-ink-750 text-ink-100 shadow-[inset_0_1px_0_0_rgb(255_255_255/0.06)]'
                  : 'text-ink-400 hover:text-ink-200',
              )}
            >
              {entry.label}
              {entry.count !== undefined && (
                <span className="ml-1.5 font-mono text-[11px] text-ink-500">{entry.count}</span>
              )}
            </button>
          ))}
        </div>
      </nav>

      {actionError && <ErrorNote>{actionError}</ErrorNote>}

      {tab === 'run' && isDm && (
        <DmDashboard campaignId={campaignId} campaignName={campaign.name} />
      )}

      {tab === 'party' && isDm && (
        <section>
          <SectionHeading>Quick join</SectionHeading>
          <Panel>
            {quickJoinInvite ? (
              <QuickJoinQr invite={quickJoinInvite} />
            ) : (
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-ink-300">No active join code yet.</p>
                  <p className="mt-1 text-xs text-ink-500">Create one so players can scan and join the campaign in one tap.</p>
                </div>
                <Button variant="primary" busy={creatingQuickJoin} onClick={createQuickJoinInvite}>
                  Create QR invite
                </Button>
              </div>
            )}
          </Panel>
        </section>
      )}

      {tab === 'party' && (
      <section>
        <SectionHeading
          action={
            myRole !== 'guest' && (
              <div className="flex gap-2">
                <Button size="sm" onClick={() => navigate(`/campaigns/${campaignId}/characters/import`)}>
                  Import a PDF
                </Button>
                <Button size="sm" onClick={() => navigate(`/campaigns/${campaignId}/characters/new`)}>
                  Add character
                </Button>
              </div>
            )
          }
        >
          The party
        </SectionHeading>

        {characters.length === 0 ? (
          <EmptyState
            title="No characters yet"
            description="Once players roll up their characters, the party's health, AC, and passive Perception show up here at a glance."
          />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {characters.map((character) => (
              <PartyCard key={character.id} character={character} />
            ))}
          </div>
        )}
      </section>
      )}

      {tab === 'story' && <StoryTab campaignId={campaignId} myRole={myRole} />}
      {tab === 'npcs' && <NpcTab campaignId={campaignId} myRole={myRole} />}
      {tab === 'loot' && (
        <LootTab campaignId={campaignId} myRole={myRole} characters={characters} />
      )}
      {tab === 'funds' && <FundsTab campaignId={campaignId} myRole={myRole} />}
      {tab === 'initiative' && <InitiativeTab campaignId={campaignId} myRole={myRole} />}
      {tab === 'assistant' && isDm && <AssistantTab campaignId={campaignId} />}
      {tab === 'dice' && (
        <RollFeed
          campaignId={campaignId}
          myRole={myRole}
          rolls={feed.rolls}
          loading={feed.loading}
          onCleared={feed.reset}
        />
      )}

      {tab === 'table' && (
      <div className="grid gap-6 lg:grid-cols-2">
        <section>
          <SectionHeading>At the table</SectionHeading>
          <Panel className="divide-y divide-ink-700/60">
            {members.map((member) => (
              <MemberRow
                key={member.id}
                member={member}
                isDm={isDm}
                isSelf={member.id === user?.id}
                onRoleChange={(role) => guard(() => api.setMemberRole(campaignId, member.id, role))}
                onRemove={() => guard(() => api.removeMember(campaignId, member.id))}
              />
            ))}
          </Panel>
        </section>

        {isDm && (
          <div className="space-y-6">
            <InvitesSection campaignId={campaignId} onError={setActionError} />

            <section>
              <SectionHeading>Backup</SectionHeading>
              <Panel className="flex flex-wrap items-center justify-between gap-3 p-4">
                <p className="min-w-0 text-sm text-ink-400">
                  Download the whole campaign — characters, story, NPCs, funds, loot, and rolls —
                  as one JSON file you can keep anywhere.
                </p>
                <a
                  href={`/api/campaigns/${campaignId}/export`}
                  className="shrink-0 rounded-xl border border-ink-600/80 bg-ink-750 px-4 py-2 text-sm text-ink-100 transition-colors hover:bg-ink-700"
                >
                  Export campaign
                </a>
              </Panel>
            </section>
          </div>
        )}
      </div>
      )}

      {myRole !== 'guest' && (
        <DiceRoller
          campaignId={campaignId}
          characterId={myCharacters.length === 1 ? myCharacters[0]!.id : null}
          canRollPrivately={isDm}
          onRolled={feed.push}
        />
      )}
    </div>
  )
}

function MemberRow({
  member, isDm, isSelf, onRoleChange, onRemove,
}: {
  member: CampaignMember
  isDm: boolean
  isSelf: boolean
  onRoleChange: (role: 'player' | 'guest') => void
  onRemove: () => void
}) {
  const canManage = isDm && member.role !== 'dm'

  return (
    <div className="flex items-center gap-3 px-4 py-3">
      <span aria-hidden className="grid size-8 shrink-0 place-items-center rounded-full bg-ink-750 font-display text-xs text-ink-300">
        {member.displayName.slice(0, 2).toUpperCase()}
      </span>

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm text-ink-100">
          {member.displayName}
          {isSelf && <span className="ml-1.5 text-xs text-ink-500">(you)</span>}
        </p>
        {member.email && <p className="truncate text-xs text-ink-500">{member.email}</p>}
      </div>

      {canManage ? (
        <>
          <Select
            aria-label={`Role for ${member.displayName}`}
            value={member.role}
            onChange={(e) => onRoleChange(e.target.value as 'player' | 'guest')}
            className="h-8 w-28 shrink-0 py-0 text-xs"
          >
            <option value="player">Player</option>
            <option value="guest">Guest</option>
          </Select>
          <Button variant="ghost" size="sm" className="shrink-0" onClick={onRemove} aria-label={`Remove ${member.displayName}`}>
            Remove
          </Button>
        </>
      ) : (
        <Badge tone={ROLE_TONE[member.role]}>{member.role}</Badge>
      )}
    </div>
  )
}

function InvitesSection({ campaignId, onError }: { campaignId: string; onError: (message: string) => void }) {
  const { data, loading, reload } = useAsync(() => api.listInvites(campaignId), [campaignId])
  const [creating, setCreating] = useState(false)
  const [role, setRole] = useState<'player' | 'guest'>('player')
  const [busy, setBusy] = useState(false)

  const active = data?.invites.filter((invite) => !invite.revokedAt) ?? []

  async function create() {
    setBusy(true)
    try {
      await api.createInvite(campaignId, { role, maxUses: null, expiresInDays: 14 })
      setCreating(false)
      reload()
    } catch (err) {
      onError(err instanceof ApiError ? err.message : 'Could not create the invite')
    } finally {
      setBusy(false)
    }
  }

  return (
    <section>
      <SectionHeading action={<Button size="sm" onClick={() => setCreating(true)}>New invite</Button>}>
        Invite codes
      </SectionHeading>

      <Panel className="divide-y divide-ink-700/60">
        {loading ? (
          <p className="px-4 py-6 text-center text-sm text-ink-500">Loading…</p>
        ) : active.length === 0 ? (
          <p className="px-4 py-6 text-center text-sm text-ink-500">
            No active codes. Create one to bring a player in.
          </p>
        ) : (
          active.map((invite) => (
            <InviteRow
              key={invite.code}
              invite={invite}
              onRevoke={async () => {
                try {
                  await api.revokeInvite(campaignId, invite.code)
                  reload()
                } catch (err) {
                  onError(err instanceof ApiError ? err.message : 'Could not revoke')
                }
              }}
            />
          ))
        )}
      </Panel>

      <Modal open={creating} title="New invite code" onClose={() => setCreating(false)}>
        <div className="space-y-4">
          <Field label="Joins as" hint="Guests get read-only access; players get a character sheet.">
            {(id) => (
              <Select id={id} value={role} onChange={(e) => setRole(e.target.value as 'player' | 'guest')}>
                <option value="player">Player</option>
                <option value="guest">Guest / spectator</option>
              </Select>
            )}
          </Field>
          <p className="text-xs text-ink-500">Codes expire after 14 days and can be used any number of times until then.</p>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setCreating(false)}>Cancel</Button>
            <Button variant="primary" busy={busy} onClick={create}>Create code</Button>
          </div>
        </div>
      </Modal>
    </section>
  )
}

function QuickJoinQr({ invite }: { invite: Invite }) {
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null)
  const link = `${window.location.origin}/join/${invite.code}`

  useEffect(() => {
    let cancelled = false
    QRCode.toDataURL(link, {
      margin: 1,
      width: 220,
      errorCorrectionLevel: 'M',
    })
      .then((value: string) => {
        if (!cancelled) setQrDataUrl(value)
      })
      .catch(() => {
        if (!cancelled) setQrDataUrl(null)
      })

    return () => {
      cancelled = true
    }
  }, [link])

  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
      <div className="flex justify-center rounded-xl bg-white p-3">
        {qrDataUrl ? (
          <img src={qrDataUrl} alt={`Join campaign QR code for ${invite.code}`} className="size-40 rounded-md" />
        ) : (
          <div className="grid size-40 place-items-center rounded-md bg-ink-950 text-center text-xs text-ink-500">
            Generating…
          </div>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <p className="text-xs uppercase tracking-[0.18em] text-ink-500">Quick join</p>
        <p className="mt-2 font-mono text-base tracking-[0.2em] text-gold-400">{invite.code}</p>
        <p className="mt-2 text-sm text-ink-400">A player can scan this on their phone and go straight to the join page.</p>
        <a href={link} className="mt-3 inline-flex text-sm text-gold-400 underline" target="_blank" rel="noreferrer">
          Open join link
        </a>
      </div>
    </div>
  )
}

function InviteRow({ invite, onRevoke }: { invite: Invite; onRevoke: () => void }) {
  const [copied, setCopied] = useState(false)
  const [qrOpen, setQrOpen] = useState(false)
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null)
  const link = `${window.location.origin}/join/${invite.code}`

  useEffect(() => {
    if (!qrOpen) return

    let cancelled = false
    QRCode.toDataURL(link, {
      margin: 1,
      width: 220,
      errorCorrectionLevel: 'M',
    })
      .then((value: string) => {
        if (!cancelled) setQrDataUrl(value)
      })
      .catch(() => {
        if (!cancelled) setQrDataUrl(null)
      })

    return () => {
      cancelled = true
    }
  }, [link, qrOpen])

  async function copy() {
    try {
      await navigator.clipboard.writeText(link)
      setCopied(true)
      setTimeout(() => setCopied(false), 1800)
    } catch {
      // Clipboard is unavailable over plain http on some browsers; the code is
      // on screen either way.
    }
  }

  return (
    <>
      <div className="flex items-center gap-3 px-4 py-3">
        <div className="min-w-0 flex-1">
          <p className="font-mono text-sm tracking-widest text-gold-400">{invite.code}</p>
          <p className="mt-0.5 text-xs text-ink-500">
            {invite.role} · used {invite.uses}
            {invite.maxUses ? `/${invite.maxUses}` : ''} time{invite.uses === 1 ? '' : 's'}
            {invite.expiresAt && ` · expires ${new Date(invite.expiresAt).toLocaleDateString()}`}
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={() => setQrOpen(true)}>QR code</Button>
        <Button variant="ghost" size="sm" onClick={copy}>{copied ? 'Copied' : 'Copy link'}</Button>
        <Button variant="ghost" size="sm" onClick={onRevoke}>Revoke</Button>
      </div>

      <Modal open={qrOpen} title="Scan to join" onClose={() => setQrOpen(false)}>
        <div className="space-y-4">
          <div className="flex justify-center rounded-xl bg-white p-3">
            {qrDataUrl ? (
              <img src={qrDataUrl} alt={`Join campaign QR code for ${invite.code}`} className="size-44 rounded-md" />
            ) : (
              <div className="grid size-44 place-items-center rounded-md bg-ink-950 text-center text-xs text-ink-500">
                Generating…
              </div>
            )}
          </div>
          <p className="text-center font-mono text-sm tracking-[0.2em] text-gold-400">{invite.code}</p>
          <p className="text-center text-xs text-ink-500">Scan this to join the campaign in a browser.</p>
          <div className="flex justify-end">
            <Button variant="ghost" onClick={() => setQrOpen(false)}>Close</Button>
          </div>
        </div>
      </Modal>
    </>
  )
}
